import React, { useCallback, useEffect, useState } from 'react';
import {
  BillingInfo,
  SimplePicker,
  StoredPaymentMethodSelect,
  TransactionCategory,
  useBillingInfo,
} from '@shipstation/financial-services-ui-components';
import classNames from 'clsx';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { TextType } from 'ui-components/components/text';

import { messages } from './messages';
import style from './style.module.less';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { PaymentMethod } from '../../../../../enums/payment-method';
import { useFsuiContext } from '../../../../../financial-services-ui/fsui-context-provider';
import fsuiStyles from '../../../../../financial-services-ui/fsui-styles.module.less';
import { getBillingInfoFromPaymentMethod, getBillingInfoFromShipFrom } from '../../../../../financial-services-ui/mapping';
import { showGenericErrorNotification } from '../../../../../financial-services-ui/notifications';
import { PickerTitle } from '../../../../../financial-services-ui/picker-title';
import { useLocale } from '../../../../../intl/hooks';
import { AddressDropdown } from '../../../../../pages/settings/payment-and-subscription/address-dropdown';
import { TaxIdentifierSection } from '../../../../../pages/settings/payment-and-subscription/components/tax-identifier-section';
import Global from '../../../../../store/global-selectors';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { FORM_REQUIREMENTS } from '../../../../utilities/constants';
import { BUSINESS, PERSONAL } from '../../../constants';
import { PickupAddressCheckbox } from '../../components/billing-and-pickup/pickup-address-checkbox';
import { WalletPickupAddress } from '../../components/billing-and-pickup/wallet-pickup-address';
import { RequirementGuard } from '../../components/requirement-guard';
import { useWalletRegistrationFormContext } from '../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../contexts/wallet-registration-modal-context';

/**
 * @typedef {import('@shipstation/financial-services-ui-components').CreditCardConfig} CreditCardConfig
 * @typedef {import('@shipstation/financial-services-ui-components').PayByBankConfig} PayByBankConfig
 */

/**
 * @param {object} props
 * @param {CreditCardConfig} props.creditCardConfig - Configuration for credit card payment
 * @param {PayByBankConfig} props.payByBankUsConfig - Configuration for Pay by Bank US
 * @param {(requirementId:string, value: boolean) => void} props.updateRequirementStatus - Function to update the status of requirements in the context
 * @param {() => void} props.onExistingPaymentMethodSelected - Callback when an existing payment method is selected
 * @param {React.MutableRefObject} props.setFsuiBillingInfoRef - Function to set the FSUI billing info reference
 * @returns {JSX.Element}
 */
export const WalletPaymentDetails = ({
  creditCardConfig,
  payByBankUsConfig,
  updateRequirementStatus,
  onExistingPaymentMethodSelected,
  setFsuiBillingInfoRef,
}) => {
  const shipFroms = useSelector(Global.selectSellerShipFroms) || emptyArray;
  const initialBillingInfo = shipFroms?.length ? shipFroms.find((shipFrom) => shipFrom.isDefault) : undefined;
  const intl = useIntl();
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const { billingInfo, setBillingInfo, isEditing } = useBillingInfo();
  const [selectedAddress, setSelectedAddress] = useState(initialBillingInfo || emptyObject);

  const { locale } = useLocale();
  const seller = useSelector((state) => state.seller) || emptyObject;
  const { homeCountry, name, companyName, phone } = seller || emptyObject;

  const { apiConfig, preloadedSession, setPaymentMethodId } = useFsuiContext();

  const { walletToRegister: wallet, setPaymentType, isPickupSameAsBilling, setIsPickupSameAsBilling } = useWalletRegistrationModalContext();

  const { setPerformFormValidation } = useWalletRegistrationFormContext();

  const findWalletPaymentMethod = useCallback((methods = []) => {
    return methods.find((method) => method.assignedPreferences?.includes(TransactionCategory.AddFunds)) || null;
  }, []);

  const updateBillingInfo = useCallback(
    (method) => {
      if (!method) {
        setBillingInfo(
          {
            countryCode: homeCountry,
          },
          false,
        );
        return;
      }
      setBillingInfo(
        getBillingInfoFromPaymentMethod(method, {
          name,
          homeCountry,
          phone,
          companyName,
        }),
        true,
      );
    },
    [setBillingInfo, homeCountry, name, companyName, phone],
  );

  const updatePaymentMethods = useCallback(async () => {
    setIsLoadingPaymentMethods(true);
    try {
      const { listActivePaymentMethods } = apiConfig;
      const methodsResponse = await listActivePaymentMethods();
      const methods = [...(methodsResponse?.activePaymentMethods || []), ...(methodsResponse?.recentPaymentMethods || [])];

      setPaymentMethods(methodsResponse);
      const walletPaymentMethod = findWalletPaymentMethod(methods);
      setSelectedPaymentMethod(walletPaymentMethod);
      updateBillingInfo(walletPaymentMethod);
      setPaymentMethodId(walletPaymentMethod?.id);
      setIsAdding(methods.length === 0);
    } catch {
      showGenericErrorNotification();
      setIsAdding(true);
      setPaymentMethods({ activePaymentMethods: [], recentPaymentMethods: [] });
      setSelectedPaymentMethod(null);
      updateBillingInfo(null);
      setPaymentMethodId(null);
    } finally {
      setIsLoadingPaymentMethods(false);
      setIsLoadingInitialData(false);
    }
  }, [apiConfig, findWalletPaymentMethod, updateBillingInfo, setPaymentMethodId]);

  useEffect(() => {
    updatePaymentMethods();
  }, [updatePaymentMethods]);

  useEffect(() => {
    // oxlint-disable-next-line no-param-reassign
    setFsuiBillingInfoRef.current = setBillingInfo;
  }, [setBillingInfo, setFsuiBillingInfoRef]);

  const handleClickOnSwitchMode = useCallback(() => {
    const newIsAdding = !isAdding;

    if (newIsAdding) {
      setSelectedPaymentMethod(null);
      updateBillingInfo(null);
      setPaymentMethodId(null);
    } else {
      const walletPaymentMethod = findWalletPaymentMethod(paymentMethods?.activePaymentMethods || []);
      setSelectedPaymentMethod(walletPaymentMethod);
      updateBillingInfo(walletPaymentMethod);
      setPaymentType(walletPaymentMethod?.paymentMethod === PaymentMethod.ACH ? PaymentMethod.ACH : PaymentMethod.AdyenCreditCard);
      setPaymentMethodId(walletPaymentMethod?.id || null);
      onExistingPaymentMethodSelected();
    }
    setIsAdding(newIsAdding);
  }, [
    isAdding,
    findWalletPaymentMethod,
    paymentMethods,
    updateBillingInfo,
    setPaymentType,
    setPaymentMethodId,
    onExistingPaymentMethodSelected,
  ]);

  const handlePaymentMethodChange = useCallback(
    (method) => {
      setSelectedPaymentMethod(method);
      updateBillingInfo(method);
      onExistingPaymentMethodSelected();
      setPaymentMethodId(method?.id || null);
      if (method) {
        setPaymentType(method.paymentMethod === PaymentMethod.ACH ? PaymentMethod.ACH : PaymentMethod.AdyenCreditCard);
      }
    },
    [updateBillingInfo, setPaymentType, onExistingPaymentMethodSelected, setPaymentMethodId],
  );

  const handleAddressChange = useCallback(
    (addressSelection) => {
      setBillingInfo(getBillingInfoFromShipFrom(addressSelection?.originAddress) || emptyObject, true);
      setSelectedAddress(addressSelection);
    },
    [setBillingInfo],
  );

  if (isLoadingInitialData) {
    return <LoadingIndicator className={fsuiStyles.loader} data-testid='wallet-registration-loading' />;
  }

  const hasPaymentMethodsToPick = !!(paymentMethods?.activePaymentMethods?.length || paymentMethods?.recentPaymentMethods?.length);

  return (
    <div>
      <PickerTitle
        className={style['payment-method-header-padding']}
        hasPaymentMethodsToPick={hasPaymentMethodsToPick}
        isAdding={isAdding}
        labelTextType={TextType.Label}
        onClick={handleClickOnSwitchMode}
      />
      {isAdding ? (
        <SimplePicker
          acceptedPaymentTypes={preloadedSession?.eligiblePaymentMethodTypes || emptyArray}
          creditCardConfig={creditCardConfig}
          currencyCode={wallet?.currencyCode}
          locale={locale}
          onPaymentTypeChange={setPaymentType}
          payByBankConfig={payByBankUsConfig}
          showTitle={false}
        />
      ) : (
        <StoredPaymentMethodSelect
          className={classNames(
            hasPaymentMethodsToPick ? fsuiStyles.picker : fsuiStyles['picker-no-content'],
            style['payment-method-dropdown-padding'],
          )}
          disabled={!hasPaymentMethodsToPick}
          emptyStateMessage={intl.formatMessage(messages.noPaymentMethodsAvailable)}
          hasError={false}
          initialValue={selectedPaymentMethod}
          isFullWidth
          isLoading={isLoadingPaymentMethods}
          name='paymentMethodSelect'
          onChange={handlePaymentMethodChange}
          paymentMethods={paymentMethods}
          placeholder={intl.formatMessage(messages.selectPaymentMethodPlaceholder)}
          transactionCategory={TransactionCategory.AddFunds}
        />
      )}

      {!isLoadingPaymentMethods && (
        <>
          <RequirementGuard requirements={FORM_REQUIREMENTS.BILLING_ADDRESS}>
            <div className={classNames(fsuiStyles['billing-info-wrapper'], style['billing-info'])}>
              {isEditing && !!shipFroms?.length ? (
                <AddressDropdown
                  className={style['address-dropdown']}
                  onAddressChange={handleAddressChange}
                  selectedAddress={selectedAddress}
                  shipFroms={shipFroms}
                />
              ) : null}
              <BillingInfo key={selectedPaymentMethod?.id} setPerformFormValidation={setPerformFormValidation} />
            </div>
            <RequirementGuard requirements={FORM_REQUIREMENTS.PICKUP_ADDRESS}>
              <PickupAddressCheckbox isPickupSameAsBilling={isPickupSameAsBilling} setIsPickupSameAsBilling={setIsPickupSameAsBilling} />
            </RequirementGuard>
          </RequirementGuard>
          <RequirementGuard requirements={FORM_REQUIREMENTS.PICKUP_ADDRESS}>
            <div className={style.spacer} />
            {!isPickupSameAsBilling && (
              <WalletPickupAddress
                accountType={billingInfo?.company ? BUSINESS : PERSONAL}
                className={style['pickup-wrapper']}
                headerTextType={TextType.H2}
                markFormComplete={updateRequirementStatus}
              />
            )}
          </RequirementGuard>
          <RequirementGuard requirements={FORM_REQUIREMENTS.TAX_ID_INFORMATION}>
            <TaxIdentifierSection
              className={style['tax-identifier-wrapper']}
              formClassName={style['tax-identifier-full-width']}
              sectionClassName={style['tax-identifier-section']}
            />
          </RequirementGuard>
        </>
      )}
    </div>
  );
};
